import React, { useState, useEffect, useCallback } from 'react'
import DashboardLayout from '../../../layouts/Dashboard'
import { Col, Row, Button, Badge, Spinner, Image, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { Plus, Warning, Pencil, UserCircle } from 'phosphor-react'
import api from '../../../services/api'
import { toast } from 'react-toastify'

import styles from './styles.module.css'
import { useLoading } from '../../../hooks/LoadingContext'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../components/SearchInput'

interface IManager {
  name: string
  user_id: string
  birth: Date
  state: string
  city: string
  profilePicture: { url: string }
  account_validated: boolean
}
const Motoristas: React.FC = () => {
  const [managers, setManagers] = useState<IManager[]>()
  const { isLoading, setIsLoading } = useLoading()
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const [search, setSearch] = useState('')
  const [lastSearch, setLastSearch] = useState('')

  const navigate = useNavigate()

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl =
      '/company/delivery-managers?driverInfo=true&profile_picture=true&limit=12&show_invalid=true'
    if (search) apiUrl += '&name=' + search

    const offset = managers && !isSearchDifferent ? managers.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      const managersData = data.delivery_managers
      if (!managersData.length) {
        if (isSearchDifferent) setManagers(data)
        setHasMoreData(false)
      } else {
        if (!managers) setManagers(managersData)
        else setManagers(isSearchDifferent ? managersData : managers.concat(managersData))
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, managers, hasMoreData])

  useEffect(() => {
    if (!managers && hasMoreData) loadData()
  }, [loadData, hasMoreData, managers])

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
          document.documentElement.offsetHeight ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Gestores de entregas">
      <LoadingSpinner text="Buscando gestores de entrega. Aguarde..." />
      <Row style={{ marginInline: 25 }}>
        <SearchInput
          placeholder="Digite o nome do gestor de entregas"
          searchValue={search}
          setValue={setSearch}
          handleSearch={loadData}
          showOptions={false}
        />
        <Col
          style={{ display: 'flex', justifyContent: 'right' }}
          className="justify-content-md-end justify-content-center"
        >
          <Button
            className={`${styles.new_manager_button} px-3 px-md-5`}
            onClick={() => navigate('/empresa/gestores-entregas/novo')}
          >
            <Plus
              style={{ marginRight: 4, marginBottom: 4 }}
              color="#fff"
              size={15}
              weight="bold"
            />
            Novo gestor de entregas
          </Button>
        </Col>
      </Row>
      <Row style={{ marginInline: 30, display: 'flex', justifyContent: 'left' }}>
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!managers?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhum
            gestor de entregas encontrado!
          </Badge>
        )}
        {managers &&
          managers.length > 0 &&
          managers?.map((driver, index) => (
            <Col key={index} style={{ padding: 8 }} className="col-12 col-md-6 col-lg-6 col-xl-4">
              <div className={styles.manager_card}>
                <Col md={5} style={{ alignSelf: 'center' }} className="col-5">
                  {driver.profilePicture ? (
                    <Image
                      style={{
                        objectFit: 'cover',
                        width: '100%',
                        aspectRatio: '1',
                        borderRadius: '50%',
                      }}
                      fluid={false}
                      src={driver.profilePicture.url}
                    />
                  ) : (
                    <UserCircle size={'100%'} />
                  )}
                </Col>
                <Col md={7} className={`col-7 ${styles.manager_info}`}>
                  <Row style={{ textAlign: 'center', margin: 0 }}>
                    <span className={styles.manager_name}>{driver.name}</span>
                    <span
                      style={{
                        fontWeight: '400',
                        fontSize: 12,
                      }}
                    >
                      {driver.city} - {driver.state}
                    </span>
                    {!driver.account_validated && (
                      <div>
                        <Badge bg="danger" style={{ marginTop: 5, fontSize: 10 }}>
                          Conta não validada
                        </Badge>
                      </div>
                    )}
                  </Row>
                  <Row className={styles.btn_row}>
                    <OverlayTrigger
                      placement="bottom"
                      overlay={<Tooltip>Editar gestor de entregas</Tooltip>}
                    >
                      <div
                        className={styles.edit_manager_button}
                        onClick={() =>
                          navigate(`/empresa/gestores-entregas/editar/${driver.user_id}`)
                        }
                      >
                        <Pencil color="#053C5E" size={22} />
                      </div>
                    </OverlayTrigger>
                  </Row>
                </Col>
              </div>
            </Col>
          ))}
      </Row>
    </DashboardLayout>
  )
}

export default Motoristas
